import type { TFunction } from '@mukil/i18n';
import { FormField, Input, StyledSelect } from '@jumpstone/ui-react';
import type {
  EmployeeFormDraft,
  EmployeeFormFieldError,
} from './employee_form_validation';
import {
  needs_start_date_hint,
  WORKER_TYPE_ORDER,
} from '../../lib/worker_types';
import { employment_type_label } from '../../lib/worker_type_labels';
import type { EmploymentType } from '../../data/types';

const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

/** One-line explanation of the selected type, shown as the picker's help text. */
function type_hint(t: TFunction, type: EmploymentType): string {
  switch (type) {
    case 'full_time':
      return t('employees.form_employment_type_hint_full_time');
    case 'part_time':
      return t('employees.form_employment_type_hint_part_time');
    case 'contract':
      return t('employees.form_employment_type_hint_contract');
    case 'contractor':
      return t('employees.form_employment_type_hint_contractor');
  }
}

function fte_error_text(
  t: TFunction,
  error: EmployeeFormFieldError | undefined,
): string | undefined {
  if (error === 'fte_required') return t('employees.form_error_fte_required');
  if (error === 'fte_out_of_range') {
    return t('employees.form_error_fte_out_of_range');
  }
  return undefined;
}

export interface EmployeeFormWorkerTypeFieldsProps {
  draft: EmployeeFormDraft;
  /** The SAVED worker type (edit form), `null` on create. */
  original_employment_type: EmploymentType | null;
  fte_error: EmployeeFormFieldError | undefined;
  t: TFunction;
  on_type_change: (type: EmploymentType) => void;
  on_fte_change: (value: string) => void;
}

/**
 * Worker-type picker (+ the part-time "% of full-time" field). Pure
 * presentation: every value/handler comes from the dialog. The type change
 * goes through `on_type_change` (not the generic setter) so the dialog can
 * clear the FTE and its error when the worker stops being part-time.
 */
export function EmployeeFormWorkerTypeFields({
  draft,
  original_employment_type,
  fte_error,
  t,
  on_type_change,
  on_fte_change,
}: EmployeeFormWorkerTypeFieldsProps): React.JSX.Element {
  return (
    <>
      <FormField
        label={t('employees.form_employment_type')}
        helpText={type_hint(t, draft.employment_type)}
      >
        <StyledSelect<EmploymentType>
          value={draft.employment_type}
          onChange={on_type_change}
          options={WORKER_TYPE_ORDER.map((type) => ({
            value: type,
            label: employment_type_label(t, type),
          }))}
          ariaLabel={t('employees.form_employment_type')}
          triggerClassName={SELECT_TRIGGER_CLASSES}
        />
      </FormField>

      {needs_start_date_hint(
        original_employment_type,
        draft.employment_type,
      ) && (
        <p
          className="nv-help sm:col-span-2"
          data-testid="employee-form-contractor-to-employee-start-date-hint"
        >
          {t('employees.form_contractor_to_employee_start_date_hint')}
        </p>
      )}

      {draft.employment_type === 'part_time' && (
        <FormField
          label={t('employees.form_fte_percent')}
          htmlFor="employee-form-fte-percent"
          helpText={t('employees.form_fte_percent_hint')}
          required
          error={fte_error_text(t, fte_error)}
        >
          <Input
            id="employee-form-fte-percent"
            value={draft.fte_percent}
            inputMode="numeric"
            onChange={(e) => {
              on_fte_change(e.target.value);
            }}
          />
        </FormField>
      )}
    </>
  );
}
